// Demo client registry + active-client resolution.
//
// The active client is resolved ONCE per page load (from localStorage, default
// Demo Arena) and is then a stable constant for the lifetime of the session —
// constants.ts derives module-level exports from it and several fixtures cache,
// so switching clients persists the choice and RELOADS the page for a clean
// re-seed rather than trying to hot-swap the whole console in place.

import type { DemoClient } from './types'
import { arenaClient, ARENA_CLIENT_ID } from './arena'
import { stoneMountainClient, STONE_MOUNTAIN_CLIENT_ID } from './stoneMountain'

export type { DemoClient } from './types'
export { ARENA_CLIENT_ID, STONE_MOUNTAIN_CLIENT_ID }

const STORAGE_KEY = 'parkify_demo_client'

// Every demo tenant that exists, in switcher order. Stone Mountain leads.
const ALL_CLIENTS: DemoClient[] = [stoneMountainClient, arenaClient]

// Build-time single-tenant pin. When the bundle is built with VITE_DEMO_CLIENT
// set to a client id (e.g. the `deploy:demo:stone` script for the client-facing
// Stone Mountain deploy), the registry collapses to JUST that tenant: no other
// client is bundled into the switcher, the saved-choice override is ignored, and
// installDemo() drops the switcher entirely (see ../index.ts — it only mounts
// when DEMO_CLIENTS.length > 1). Unset → the internal multi-tenant demo with the
// switcher, defaulting to Stone Mountain.
const PINNED_ID = (import.meta.env.VITE_DEMO_CLIENT as string | undefined) || ''
const PINNED = ALL_CLIENTS.find(c => c.id === PINNED_ID)

// The clients this build exposes. A pin reduces it to one; the switcher and any
// localStorage override key off this list, so a pinned build can never surface
// another tenant.
export const DEMO_CLIENTS: DemoClient[] = PINNED ? [PINNED] : ALL_CLIENTS

// Default to the pinned tenant when single-tenant, else Stone Mountain Park —
// the current prospect demo — so the URL lands there with no clicking.
const DEFAULT_CLIENT = PINNED ?? stoneMountainClient

function resolveActive(): DemoClient {
  // A build-time pin is absolute — never let a stale localStorage choice from a
  // previous (multi-tenant) build reach a different tenant in a client build.
  if (PINNED) return PINNED
  let id: string | null = null
  try { id = localStorage.getItem(STORAGE_KEY) } catch { /* private mode */ }
  return DEMO_CLIENTS.find(c => c.id === id) ?? DEFAULT_CLIENT
}

// Resolved once at import — stable for the lifetime of the page.
export const ACTIVE: DemoClient = resolveActive()

export function activeClientId(): string {
  return ACTIVE.id
}

// Persist the chosen client and reload so the whole console re-seeds from it.
export function setActiveDemoClient(id: string): void {
  try { localStorage.setItem(STORAGE_KEY, id) } catch { /* private mode */ }
  window.location.reload()
}
